import { Editor } from 'slate';
import Tool, { type ToolArgsWithController } from '../../../tools/Tool';
import TableUtils from '../../TablePlugin/TableUtils';
import type AhaEditorV2Controller from '../../../controllers/AhaEditorV2Controller';
import { type Marks } from '../../../types/aha-editor';

// General purpose tool for toggling formatting marks.
// It's intended to be subclassed, not used directly.
class ToolMark extends Tool {
  static mark: keyof Marks;

  static addMark(
    editor: Editor,
    controller: AhaEditorV2Controller,
    markValue: boolean | string | number
  ): void {
    const tableSelection = controller.state.pluginState.tableSelection;
    const tableWithinSelection = TableUtils.isTableWithinSelection(editor);

    const addMark = () => {
      Editor.addMark(editor, this.mark, markValue);
    };

    if (tableWithinSelection && tableSelection) {
      TableUtils.applyToTable(editor, tableSelection, addMark, {
        hangRange: true,
      });
    } else {
      addMark();
    }
  }

  static removeMark(editor: Editor, controller: AhaEditorV2Controller): void {
    const tableSelection = controller.state.pluginState.tableSelection;
    const tableWithinSelection = TableUtils.isTableWithinSelection(editor);

    const removeMark = () => Editor.removeMark(editor, this.mark);

    if (tableWithinSelection && tableSelection) {
      TableUtils.applyToTable(editor, tableSelection, removeMark, {
        hangRange: true,
      });
    } else {
      removeMark();
    }
  }

  static action({ editor, controller }: ToolArgsWithController): void {
    if (!this.mark) {
      throw new Error(
        'You must define a static "mark" property in mark tools.'
      );
    }
    if (!editor?.selection) return;

    if (this.isActive({ editor, controller })) {
      this.removeMark(editor, controller);
    } else {
      this.addMark(editor, controller, true);
    }
  }

  static isActive({ editor, controller }: ToolArgsWithController): boolean {
    if (!editor) return false;

    const tableSelection = controller.state.pluginState.tableSelection;
    const tableWithinSelection = TableUtils.isTableWithinSelection(editor);

    if (tableWithinSelection && tableSelection) {
      const marks = TableUtils.tableSelectionMarks(editor, tableSelection);
      return !!marks?.[this.mark];
    }

    const marks = Editor.marks(editor);
    return !!marks?.[this.mark];
  }
}

export default ToolMark;
